// POST-DEPLOY-FIX-04 — Shared pilot country list (frontend).
//
// Single source of truth for ALL country dropdowns across the admin
// console (Vendors, Marketplace review, Ad-slot targeting, future
// Calendar/Wishlist location). Mirrors `backend/app/constants_countries.py`.
//
// Entries:
//   code      ISO-3166-1 alpha-2, uppercase.
//   label     English-friendly name. Translation happens at call-site
//             via t(`countries.<code>`) when available, else falls back
//             to this literal so the UI never renders a raw code.
//   defaultLang  Best-guess BCP-47 language code that we WILL prefill
//             when this country is picked. The user can override.
//
// Sorted alphabetically by code for predictable scan-ability.

export const COUNTRIES = [
  { code: "AE", label: "United Arab Emirates", defaultLang: "en" },
  { code: "AR", label: "Argentina", defaultLang: "es" },
  { code: "AT", label: "Austria", defaultLang: "de" },
  { code: "AU", label: "Australia", defaultLang: "en" },
  { code: "BE", label: "Belgium", defaultLang: "fr" },
  { code: "BR", label: "Brazil", defaultLang: "pt-br" },
  { code: "CA", label: "Canada", defaultLang: "en" },
  { code: "CH", label: "Switzerland", defaultLang: "de" },
  { code: "CL", label: "Chile", defaultLang: "es" },
  { code: "CN", label: "China", defaultLang: "zh" },
  { code: "CO", label: "Colombia", defaultLang: "es" },
  { code: "CZ", label: "Czech Republic", defaultLang: "cs" },
  { code: "DE", label: "Germany", defaultLang: "de" },
  { code: "DK", label: "Denmark", defaultLang: "en" },
  { code: "ES", label: "Spain", defaultLang: "es" },
  { code: "FI", label: "Finland", defaultLang: "en" },
  { code: "FR", label: "France", defaultLang: "fr" },
  { code: "GB", label: "United Kingdom", defaultLang: "en" },
  { code: "GR", label: "Greece", defaultLang: "en" },
  { code: "HU", label: "Hungary", defaultLang: "en" },
  { code: "ID", label: "Indonesia", defaultLang: "en" },
  { code: "IE", label: "Ireland", defaultLang: "en" },
  { code: "IL", label: "Israel", defaultLang: "en" },
  { code: "IN", label: "India", defaultLang: "hi" },
  { code: "IT", label: "Italy", defaultLang: "it" },
  { code: "JP", label: "Japan", defaultLang: "ja" },
  { code: "KR", label: "South Korea", defaultLang: "ko" },
  { code: "MX", label: "Mexico", defaultLang: "es" },
  { code: "NL", label: "Netherlands", defaultLang: "nl" },
  { code: "NO", label: "Norway", defaultLang: "en" },
  { code: "PL", label: "Poland", defaultLang: "en" },
  { code: "PT", label: "Portugal", defaultLang: "pt-br" },
  { code: "RO", label: "Romania", defaultLang: "en" },
  { code: "RU", label: "Russia", defaultLang: "ru" },
  { code: "SA", label: "Saudi Arabia", defaultLang: "en" },
  { code: "SE", label: "Sweden", defaultLang: "en" },
  { code: "TR", label: "Turkey", defaultLang: "en" },
  { code: "US", label: "United States", defaultLang: "en" },
  { code: "UY", label: "Uruguay", defaultLang: "es" },
  { code: "ZA", label: "South Africa", defaultLang: "en" },
];

export const COUNTRY_CODES = COUNTRIES.map((c) => c.code);

// Look up the default language for a given country code. Returns
// undefined when the code is unknown so the caller can keep whatever
// language the user already picked.
export const defaultLangForCountry = (code) => {
  if (!code) return undefined;
  const entry = COUNTRIES.find((c) => c.code === String(code).toUpperCase());
  return entry ? entry.defaultLang : undefined;
};

// Localised country name. Falls back to the English label if the i18n
// dictionary lacks the key.
export const countryLabel = (code, t) => {
  if (!code) return "";
  const upper = String(code).toUpperCase();
  if (typeof t === "function") {
    const translated = t(`countries.${upper}`);
    if (translated && translated !== `countries.${upper}`) return translated;
  }
  const entry = COUNTRIES.find((c) => c.code === upper);
  return entry ? entry.label : upper;
};
